import type { HttpRequest, HttpResponse, HubConnection, ILogger, MessageHeaders } from "@microsoft/signalr";
import { DefaultHttpClient, HttpError, HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import { getErrorMessage } from "../api/axiosHelper";
import { checkAuthentication } from "../auth/identity";
import { logError } from "../logging";
import { refreshCookieExists } from "../utilities/cookies";
import { defined } from "../utilities/typeHelper";
import RefreshTokenService from "./RefreshTokenService";

const restartDelaysMs = [10_000, 30_000, 60_000, 120_000, 300_000];

const unauthorizedConnectionError = () => new Error("UnauthorizedConnection");

export const getRestartDelayMs = (attempt: number): number =>
  restartDelaysMs[attempt] ?? defined(restartDelaysMs.at(-1));

export const connect = async (url: string, customHeaders: MessageHeaders): Promise<HubConnection | undefined> => {
  const connection = new HubConnectionBuilder()
    .withUrl(url, {
      httpClient: new SingalRHttpClient(customHeaders),
      accessTokenFactory: async () => {
        if (!(await checkAuthentication())) {
          throw unauthorizedConnectionError();
        }
        return "";
      },
    })
    .configureLogging(LogLevel.Information)
    .withAutomaticReconnect(restartDelaysMs)
    .build();

  connection.onreconnecting(async () => {
    await checkAuthentication();
  });

  try {
    if (!(await checkAuthentication())) {
      return undefined;
    }

    await connection.start();
    return connection;
  } catch (error) {
    if (getErrorMessage(error) !== "UnauthorizedConnection") {
      logError(error, "[SignalR]");
    }
    return undefined;
  }
};

const logger: ILogger = {
  log: (logLevel: LogLevel, message: string) => {
    if (logLevel >= LogLevel.Error) {
      logError(message, "[SingalR]");
    }
  },
};

class SingalRHttpClient extends DefaultHttpClient {
  private customHeaders: MessageHeaders;

  constructor(customHeaders: MessageHeaders) {
    super(logger);
    this.customHeaders = customHeaders;
  }

  public override async send(req: HttpRequest): Promise<HttpResponse> {
    req.headers = { ...req.headers, ...this.customHeaders };
    try {
      return await super.send(req);
    } catch (error) {
      // The auth cookie can be gone while its JS-visible marker is not, so checkAuthentication skips the refresh.
      const isUnauthorized = error instanceof HttpError && error.statusCode === 401;
      if (isUnauthorized && refreshCookieExists() && (await RefreshTokenService.do())) {
        return await super.send(req);
      }
      throw error;
    }
  }
}
